import { ContractItemOptions, ContractSettingFormValues, ContractSettingItem } from '../types/contract-setting-types';
import { ContractSettingFieldDefinitions } from '../constants';
import { MasterDataSettingValueItem, ProductField } from '../types/master-data-setting-types';

export const COMMISSION_RATE_PATTERN = /^\d+(\.\d{1,2})?$/;

export const sanitizeCommissionRate = (value: number): number | undefined => {
  if (isNaN(value) || value < 0) {
    return undefined;
  }

  const stringValue = String(value);
  if (!COMMISSION_RATE_PATTERN.test(stringValue)) {
    return undefined;
  }

  return value;
};

export const createInitialFormState = (contractSettingItem?: ContractSettingItem): ContractSettingFormValues => ({
  productNameId: contractSettingItem?.productName,
  insurancePlanId: contractSettingItem?.insurancePlan,
  insurancePremiumId: contractSettingItem?.insurancePremium,
  premiumPaymentTermId: contractSettingItem?.premiumPaymentTerm,
  premiumPaymentFrequencyId: contractSettingItem?.premiumPaymentFrequency,
  premiumPaymentYearId: contractSettingItem?.premiumPaymentYear,
  effectiveDateId: contractSettingItem?.effectiveDate,
  riderCountId: contractSettingItem?.riderCount,
  commissionRate: contractSettingItem?.commissionRate ?? null,
});

export const hasCommissionRateError = (commissionRate: number | null | undefined): boolean => {
  return (
    commissionRate === null ||
    commissionRate === undefined ||
    !COMMISSION_RATE_PATTERN.test(commissionRate.toString().trim())
  );
};

export const hasMasterDataError = (formState: ContractSettingFormValues): boolean => {
  return ContractSettingFieldDefinitions.some(({ fieldKey }) => !formState[fieldKey]?._id);
};

export const PRODUCT_FIELD_MAP: Record<ProductField, keyof ContractSettingItem> = {
  [ProductField.PRODUCT_NAME]: 'productName',
  [ProductField.INSURANCE_PLAN]: 'insurancePlan',
  [ProductField.INSURANCE_PREMIUM]: 'insurancePremium',
  [ProductField.PAYMENT_TERM]: 'premiumPaymentTerm',
  [ProductField.PAYMENT_FREQUENCY]: 'premiumPaymentFrequency',
  [ProductField.PAYMENT_YEAR]: 'premiumPaymentYear',
  [ProductField.EFFECTIVE_DATE]: 'effectiveDate',
  [ProductField.RIDER_COUNT]: 'riderCount',
};

export const buildInitialOptions = (
  item: ContractSettingItem,
): Partial<Record<ProductField, MasterDataSettingValueItem[]>> => {
  const entries = Object.entries(PRODUCT_FIELD_MAP) as [ProductField, keyof ContractSettingItem][];
  const options: Partial<Record<ProductField, MasterDataSettingValueItem[]>> = {};
  entries.forEach(([field, key]) => {
    const val = item[key] as ContractItemOptions | undefined;
    if (val) {
      options[field] = [{ _id: val._id, value: val.value }];
    }
  });
  return options;
};

export const getCommissionRateInputValue = (commissionRate: number | null | undefined): string => {
  if (commissionRate === null || commissionRate === undefined) {
    return '';
  }

  return commissionRate.toString();
};

export const normalizeCommissionRateInput = (value: string): string => {
  const [integerPart, decimalPart] = value.split('.');
  const normalizedIntegerPart = integerPart.replace(/^0+(?=\d)/, '') || '0';

  if (decimalPart === undefined) {
    return normalizedIntegerPart;
  }

  return `${normalizedIntegerPart}.${decimalPart}`;
};
